import type {
  CartItem,
  GiftcardCartItem,
  GiftcardPersonalizationDelivery,
} from '~/composables/types/cart'
import type {
  GiftCardCustomization,
  OrderCreationEventItem,
  OrderCreationGiftCardItem,
} from '~/composables/types/checkout'
import type { Pricings } from '~/composables/types/event'
import { KONFETTI_GIFTCARD_SUPPLIER_ID } from '~/utils/konfettiCheckoutConstants'

/** How far into the future to push a `sendDate` that has already passed by checkout time. */
const PAST_SEND_DATE_PUSH_MINUTES = 10

/** Formats a local `Date` back into `yyyy-MM-dd HH:mm:ss` (the shape the API expects). */
function formatSendAt(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}

/**
 * `sendDate` is `yyyy-MM-dd'T'HH:mm` (GiftcardDeliveryStep's datepicker model, local time);
 * the API wants seconds and a space. A `sendDate` picked earlier can already be in the past by
 * the time the order is placed (or the cart is validated) — the API rejects past `send_at`, so
 * we push any past date to `now + PAST_SEND_DATE_PUSH_MINUTES` minutes.
 */
function toSendAt(sendDate: string): string {
  const selected = new Date(sendDate)
  if (!Number.isNaN(selected.getTime()) && selected.getTime() <= Date.now()) {
    return formatSendAt(
      new Date(Date.now() + PAST_SEND_DATE_PUSH_MINUTES * 60_000)
    )
  }
  return `${sendDate.replace('T', ' ')}:00`
}

function buildGiftCardCustomization(
  designId: string,
  delivery: GiftcardPersonalizationDelivery
): GiftCardCustomization {
  const isRecipient = delivery.mode === 'RECIPIENT'

  return {
    design: designId,
    delivery: isRecipient ? 'RECIPIENT' : 'BUYER',
    // email/send date are recipient-only fields - never forward them for a
    // self-delivered gift card, even if they're still sitting in a stale
    // draft from a prior RECIPIENT selection
    recipient_email: isRecipient ? delivery.recipientEmail || null : null,
    send_at:
      isRecipient && delivery.sendDate ? toSendAt(delivery.sendDate) : null,
    message: delivery.message || null,
    buyer_name: delivery.buyerName || null,
    recipient_name: delivery.recipientName || null,
  }
}

export type TicketCategoryLine = {
  category: string
  amount: number
}

export function ticketCategoriesFromPricings(
  pricings: Pricings | undefined
): TicketCategoryLine[] {
  return (
    pricings?.price_breakdown?.map((breakdown) => ({
      category: breakdown.title,
      amount: breakdown.total_participants,
    })) || []
  )
}

/** Same cents logic as legacy cart/order mapping (value-based vs event-based gift cards). */
export function getGiftcardLineValueInCents(item: GiftcardCartItem): number {
  const isValueBased =
    item.type === 'GIFT_CARD' && !item.id && !item.eventDescriptionId

  return isValueBased
    ? parseInt(
        item.pricings?.price_breakdown[0]?.price_per_person?.amount || '0'
      )
    : parseInt(item.pricings?.price_breakdown[0]?.total_price?.amount || '0') /
        item.quantity
}

/**
 * Supplier id for booking / embedded when the gift card line has no `supplierId`
 * (embedded query wins, then subdomain supplier fetch).
 */
export function resolveBookingGiftcardSupplierContext(args: {
  embeddedSupplierId?: string | string[] | null
  subdomainSupplierId?: string | null
}): string {
  const embedded = args.embeddedSupplierId
  if (embedded) {
    const v = Array.isArray(embedded) ? embedded[0] : embedded
    if (v) return String(v)
  }
  if (args.subdomainSupplierId) {
    return String(args.subdomainSupplierId)
  }
  return KONFETTI_GIFTCARD_SUPPLIER_ID
}

export type BuildEventCheckoutLineOptions = {
  /** When true, include total price in cents (same as place-order payload). */
  includePrice: boolean
}

/** Shared EVENT line for place-order and cart/validate. */
export function buildEventCheckoutLine(
  item: CartItem,
  options: BuildEventCheckoutLineOptions
): OrderCreationEventItem & { event?: string; date?: string } {
  if (item.type !== 'EVENT') {
    throw new Error('buildEventCheckoutLine: item must be EVENT')
  }

  const ticketCategories = ticketCategoriesFromPricings(item.pricings)
  const line: OrderCreationEventItem & { event?: string; date?: string } = {
    type: 'EVENT',
    product: item.id,
    quantity: item.quantity,
    private: item.private,
    ...(ticketCategories.length > 0 && {
      ticket_categories: ticketCategories,
    }),
  }

  if (options.includePrice) {
    line.price = parseInt(item.pricings?.price?.total_price?.amount || '0')
  }

  return line
}

export type BuildGiftCardCheckoutLineOptions = {
  /** Booking solution or embedded checkout — always attach `supplier_id`. */
  external: boolean
  /** When `external`, used if the cart line has no `supplierId`. */
  bookingSupplierFallback?: string
}

/**
 * Shared GIFT_CARD line for place-order and cart/validate.
 * - Marketplace (`external: false`): no `supplier_id`; `event` is cart line `id` (same as orders).
 * - Booking / embedded (`external: true`): always `supplier_id`; ship/ship_type match orders.
 */
export function buildGiftCardCheckoutLine(
  item: GiftcardCartItem,
  options: BuildGiftCardCheckoutLineOptions
): OrderCreationGiftCardItem {
  const addon = item.giftcardAddon
  const ship = Boolean(addon) && addon !== 'PDF'

  const line: OrderCreationGiftCardItem = {
    type: 'GIFT_CARD',
    event: item.id,
    quantity: item.quantity,
    value: getGiftcardLineValueInCents(item),
    ship,
    ...(addon && addon !== 'PDF' ? { ship_type: addon } : {}),
    ...(item.split !== undefined ? { split: item.split } : {}),
    ...(item.personalizationDelivery &&
      item.giftcardDesignId && {
        customization: buildGiftCardCustomization(
          item.giftcardDesignId,
          item.personalizationDelivery
        ),
      }),
  }

  if (options.external) {
    line.supplier_id =
      item.supplierId ||
      options.bookingSupplierFallback ||
      KONFETTI_GIFTCARD_SUPPLIER_ID
  }

  return line
}
